import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Play, Zap, Shield, CheckCircle, Clock, ChevronRight, BarChart2, List, 
  RotateCw, Plus, ToggleLeft, ArrowRight, User, Settings, CheckSquare, 
  AlertTriangle, Check, FileText, Sparkles, Building, Globe, Copy, ShieldAlert,
  ArrowLeft, Search, CheckCircle2, TrendingUp, HelpCircle
} from "lucide-react";
import { AgentTemplate } from "../data/agentTemplates";

interface NexoraXAgentWorkspaceProps {
  wizardDeployedAgent: any;
  wizardAgentForm: any;
  currentUserRole: "VISITOR" | "CLIENT" | "ADMIN";
  onShowWizard: () => void;
  onPlaySound: (freq: number, dur: number, type?: string) => void;
  triggerToast: (msg: string, type?: "success" | "info" | "warning" | "error") => void;
}

export function NexoraXAgentWorkspace({
  wizardDeployedAgent,
  wizardAgentForm,
  currentUserRole,
  onShowWizard,
  onPlaySound,
  triggerToast
}: NexoraXAgentWorkspaceProps) {
  
  // Use deployed agent or default if null
  const activeAgent = wizardDeployedAgent || {
    id: "default-support-agent",
    name: wizardAgentForm?.agentDisplayName || "NexoraX Support Assistant",
    templateName: "Customer Support Assistant Agent",
    activeTaskFlows: ["Customer Support Reply Draft", "Customer Complaint Escalation", "New Lead Follow-Up"],
    toolsList: ["Gmail / Email", "Website Forms", "Webhooks"],
    approvalLevel: wizardAgentForm?.approvalLevel || "Balanced",
    monthlySaved: 48,
    businessName: wizardAgentForm?.businessName || "Acme Enterprises"
  };

  const businessName = wizardAgentForm?.businessName || "NexoraX Enterprises";
  const toneStyle = wizardAgentForm?.toneStyle || "Professional";

  // Views system
  const [activeView, setActiveView] = useState<
    | "WORKSPACE_DASHBOARD"
    | "RUN_FIRST_TASK"
    | "TASK_INPUT_FORM"
    | "TASK_TIMELINE"
    | "OUTPUT_REVIEW"
    | "HUMAN_APPROVAL_GATE"
    | "TASK_COMPLETED_SUCCESS"
    | "TASK_HISTORY"
    | "AGENT_ANALYTICS"
    | "RECURRING_WORKFLOW_CREATOR"
  >("WORKSPACE_DASHBOARD");

  // Local state for actions
  const [agentStatus, setAgentStatus] = useState<
    "Active" | "Paused" | "Waiting for approval" | "Needs connection" | "Limited mode" | "Admin review required"
  >("Active");

  const [selectedTaskFlow, setSelectedTaskFlow] = useState<any>(null);
  const [taskFormData, setTaskFormData] = useState({
    title: "",
    goal: "",
    context: "",
    customerName: "",
    sourceText: "",
    desiredOutput: "Draft Response",
    tone: toneStyle,
    deadline: "End of Day",
    approvalRequired: true,
    notes: ""
  });

  const [timelineStep, setTimelineStep] = useState(1);
  const [generatedOutput, setGeneratedOutput] = useState("");
  const [approvalComment, setApprovalComment] = useState("");
  const [historyFilter, setHistoryFilter] = useState("ALL");

  // Seed tasks history
  const [taskHistoryList, setTaskHistoryList] = useState<any[]>([
    {
      id: "tsk-001",
      title: "Query Resolved - Freephone SLA",
      agent: "Customer Support Assistant",
      date: "2026-06-12",
      status: "COMPLETED",
      approvalStatus: "APPROVED",
      timeSaved: "20 Mins",
      outputType: "Email Draft"
    },
    {
      id: "tsk-002",
      title: "Proposal Outline Generation",
      agent: "Sales Lead Qualifier Assistant",
      date: "2026-06-11",
      status: "COMPLETED",
      approvalStatus: "APPROVED",
      timeSaved: "45 Mins",
      outputType: "PDF Layout"
    },
    {
      id: "tsk-003",
      title: "Financial Statement Compliance Check",
      agent: "Finance Operations Auditor",
      date: "2026-06-10",
      status: "ESCALATED",
      approvalStatus: "REJECTED",
      timeSaved: "0 Mins",
      outputType: "Restricted Flag"
    }
  ]);

  // Seed recurring workflows
  const [recurringWorkflows, setRecurringWorkflows] = useState<any[]>([
    {
      id: "rec-01",
      name: "Daily Support Queue Sweeper",
      agent: activeAgent.name,
      trigger: "New lead email received",
      frequency: "Daily at 08:00 AM",
      approvalRule: "High-risk items require confirmation",
      status: "ACTIVE"
    },
    {
      id: "rec-02",
      name: "Weekly Analytics Synthesizer",
      agent: activeAgent.name,
      trigger: "Every Friday at 5:00 PM",
      frequency: "Weekly",
      approvalRule: "Autopilot - Notify on completion",
      status: "ACTIVE"
    }
  ]);

  // Timeline simulation steps configuration
  const timelineSteps = [
    { num: 1, name: "Task received", exp: "Incoming parameters accepted and parsed." },
    { num: 2, name: "Context analysed", exp: "Cross-referencing client brand guidelines and voice variables." },
    { num: 3, name: "Agent preparing output", exp: "Synthesizing customized agent logic response." },
    { num: 4, name: "Safety check validation", exp: "Vetting against operational guardrails & credential masking rules." },
    { num: 5, name: "Approval check", exp: "Validating if the draft triggers human approval protocols." },
    { num: 6, name: "Draft ready", exp: "High-fidelity draft successfully saved to local registry." },
    { num: 7, name: "Awaiting review", exp: "Sent directly to approval queue." },
    { num: 8, name: "Completed", exp: "Task completed securely with permanent SLA tracking." }
  ];

  // Specific tasks based on agent template selected
  const getRecommendedTasks = () => {
    const templateName = activeAgent.templateName || "";
    if (templateName.includes("Support")) {
      return [
        { title: "Draft a customer support reply", desc: "Crafts a personalized help reply aligning with standard resolution protocols.", time: "15 mins", risk: "LOW" },
        { title: "Summarise a customer issue", desc: "Summarises 10 pages of raw ticket exchange logs with actionable bullet outputs.", time: "20 mins", risk: "LOW" },
        { title: "Create an escalation note", desc: "Compiles emergency incident logs and flags high priority telemetry events.", time: "30 mins", risk: "MEDIUM" },
        { title: "Suggest a help article", desc: "Analyzes support query and extracts best references from corporate wiki.", time: "10 mins", risk: "LOW" },
        { title: "Prepare a follow-up message", desc: "Prepares follow up message reminding consumer of resolution paths.", time: "15 mins", risk: "LOW" }
      ];
    }
    if (templateName.includes("Executive") || templateName.includes("Assistant")) {
      return [
        { title: "Create today’s priority list", desc: "Synthesizes unread messages and lists urgent timeline blocks.", time: "25 mins", risk: "LOW" },
        { title: "Summarise meeting notes", desc: "Transcribes raw shorthand minutes and writes active delegation cards.", time: "30 mins", risk: "LOW" },
        { title: "Draft a follow-up email", desc: "Drafts executive replies keeping premium brand tone constraints.", time: "15 mins", risk: "MEDIUM" },
        { title: "Prepare a daily briefing", desc: "Synthesizes performance metrics and highlights outstanding files.", time: "20 mins", risk: "LOW" },
        { title: "Schedule today reminder alerts", desc: "Configures automatic trigger reminders for upcoming executive slots.", time: "10 mins", risk: "LOW" }
      ];
    }
    if (templateName.includes("Search") || templateName.includes("Product")) {
      return [
        { title: "Compare product options", desc: "Generates comparative breakdown metrics for potential inventory items.", time: "40 mins", risk: "LOW" },
        { title: "Create supplier shortlist", desc: "Screens global suppliers based on compliance scores, credentials, and lead times.", time: "50 mins", risk: "MEDIUM" },
        { title: "Summarise product reviews", desc: "Extracts key consumer sentiment signals and lists functional flaws.", time: "30 mins", risk: "LOW" },
        { title: "Prepare buying recommendation", desc: "Constructs formal corporate recommendation layout with cost outlines.", time: "35 mins", risk: "LOW" },
        { title: "Track price comparison", desc: "Pulls price trends across digital marketplaces with alert presets.", time: "25 mins", risk: "LOW" }
      ];
    }
    if (templateName.includes("Sales")) {
      return [
        { title: "Qualify a lead", desc: "Analyzes client parameters to align with targeted budget matching tiers.", time: "20 mins", risk: "LOW" },
        { title: "Draft a sales follow-up", desc: "High-conversion follow-up pitch outlining NexoraX system value.", time: "15 mins", risk: "MEDIUM" },
        { title: "Create a proposal outline", desc: "Formats precise functional milestones and pricing options.", time: "35 mins", risk: "HIGH" },
        { title: "Prepare sales call notes", desc: "Summarizes calls and specifies next steps to close.", time: "15 mins", risk: "LOW" },
        { title: "Update CRM note", desc: "Generates formatted JSON structure for immediate system injection.", time: "10 mins", risk: "LOW" }
      ];
    }
    // Default Social Media Manager or general
    return [
      { title: "Create a weekly post plan", desc: "Generates cohesive social media calendar covering 5 channels.", time: "45 mins", risk: "LOW" },
      { title: "Draft captions", desc: "Crafts engaging captions using active brand tone and style rules.", time: "20 mins", risk: "LOW" },
      { title: "Suggest hashtags", desc: "Identifies trending structural tags for maximized campaign reach.", time: "10 mins", risk: "LOW" },
      { title: "Create platform versions", desc: "Reformats primary post for X, LinkedIn, and Instagram constraints.", time: "25 mins", risk: "LOW" },
      { title: "Send content to approval", desc: "Drafts the content campaign and submits it straight to the client gate.", time: "15 mins", risk: "MEDIUM" }
    ];
  };

  // Simulate timeline progression
  const startTimelineSimulation = (selectedTask: any) => {
    setActiveView("TASK_TIMELINE");
    setTimelineStep(1);
    onPlaySound(440, 0.15, "square");

    const timer = setInterval(() => {
      setTimelineStep((prev) => {
        if (prev >= 6) {
          clearInterval(timer);
          // Let's pre-generate synthetic output based on selected task
          let demoOutputText = `[NEXORAX GENERATED AGENT OUTPUT]\n============================\n`;
          demoOutputText += `TASK: ${selectedTask.title}\n`;
          demoOutputText += `CLIENT: ${businessName}\n`;
          demoOutputText += `TONE PRESET: ${toneStyle}\n`;
          demoOutputText += `TIMESTAMP: ${new Date().toLocaleString()}\n\n`;
          demoOutputText += `Dear recipient,\n\n`;
          demoOutputText += `We have processed your request of ${new Date().toLocaleDateString()} with high priority. Please rest assured that our dedicated systems are fully synchronized to deliver maximum efficiency.\n\n`;
          demoOutputText += `Our custom AI routines have registered this file as successfully validated. Safe settlement controls and operational SLA tracking are fully engaged for this workspace. Let us know if you require further custom updates.\n\n`;
          demoOutputText += `Best regards,\n`;
          demoOutputText += `${activeAgent.name}\n`;
          demoOutputText += `Secure Billing Provider / Verified Payment Configuration`;

          setGeneratedOutput(demoOutputText);
          onPlaySound(520, 0.3, "sine");
          setActiveView("OUTPUT_REVIEW");
          return 6;
        }
        onPlaySound(prev * 110 + 220, 0.1, "sine");
        return prev + 1;
      });
    }, 1200);
  };

  const currentTimelineStepStatus = (index: number) => {
    const currentIdx = index + 1;
    if (currentIdx < timelineStep) return "Completed";
    if (currentIdx === timelineStep) {
      if (timelineStep === 6) return "Needs approval";
      return "Running";
    }
    return "Pending";
  };

  const handleTaskFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskFormData.title.trim()) {
      triggerToast("Task Title is required", "error");
      return;
    }
    triggerToast("Configured task queued to active processing core", "info");
    startTimelineSimulation({ title: taskFormData.title });
  };

  const handleApproveAction = () => {
    // If output represents a human gate action (like External Email, Deleting data, Social posting), send to Human Approval Gate screen
    const isApprovalNeeded = 
      taskFormData.approvalRequired || 
      activeAgent.approvalLevel === "Strict" || 
      ["HIGH", "MEDIUM"].includes(selectedTaskFlow?.risk || "");

    if (isApprovalNeeded) {
      onPlaySound(330, 0.25, "triangle");
      setActiveView("HUMAN_APPROVAL_GATE");
      triggerToast("Approval Gate Tripped: Human Approval Required before sending or publishing.", "warning");
    } else {
      finalizeTaskCompletion("Approved & Autonomous Deploy");
    }
  };

  const finalizeTaskCompletion = (statusName: string) => {
    onPlaySound(660, 0.4, "sine");
    const newTask = {
      id: "tsk-" + Math.floor(Math.random() * 900 + 100),
      title: taskFormData.title || "Custom Autonomous Process",
      agent: activeAgent.templateName,
      date: new Date().toISOString().split("T")[0],
      status: "COMPLETED",
      approvalStatus: statusName === "Approved & Autonomous Deploy" ? "APPROVED" : "COMPLETED_BY_ADMIN",
      timeSaved: "30 Mins",
      outputType: "Email / Webhook dispatched"
    };

    setTaskHistoryList([newTask, ...taskHistoryList]);
    setActiveView("TASK_COMPLETED_SUCCESS");
    triggerToast("Task submitted and completed successfully!", "success");
  };

  // Convert task to recurring
  const [recurringName, setRecurringName] = useState("");
  const [recurringTrigger, setRecurringTrigger] = useState("Daily");
  const [recurringFrequency, setRecurringFrequency] = useState("At 09:00 AM");

  const handleCreateRecurringWorkflow = (e: React.FormEvent) => {
    e.preventDefault();
    const newRec = {
      id: "rec-" + Date.now(),
      name: recurringName || "Recurring Automation " + (recurringWorkflows.length + 1),
      agent: activeAgent.name,
      trigger: recurringTrigger,
      frequency: recurringFrequency,
      approvalRule: "Human Gate Enabled for High Risk",
      status: "ACTIVE"
    };
    setRecurringWorkflows([newRec, ...recurringWorkflows]);
    setActiveView("WORKSPACE_DASHBOARD");
    triggerToast(`Created recurring automated workflow successfully!`, "success");
    onPlaySound(600, 0.25, "sine");
  };

  return (
    <div className="space-y-6 pt-4 text-left">
      
      {/* Visual Identity Navigation Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-purple-500/10 pb-4 gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_rgba(34,211,238,0.8)]"></span>
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-pink-400 font-extrabold">Active Core Deployment Workspace</span>
          </div>
          <h2 className="text-xl md:text-2xl font-black font-sans text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-purple-400 uppercase tracking-tight mt-1">
            {activeAgent.name}
          </h2>
          <p className="text-xs text-slate-400 font-mono tracking-wide mt-0.5">
            Role: <span className="text-white">{activeAgent.templateName}</span> | Client Code: <span className="text-cyan-400 font-bold uppercase">{businessName}</span>
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => {
              playNexoraXSound(220, 0.1);
              setActiveView("WORKSPACE_DASHBOARD");
            }}
            className={`px-3 py-1.5 rounded-xl font-mono text-[9.5px] uppercase font-bold border transition-all ${
              activeView === "WORKSPACE_DASHBOARD"
                ? "bg-purple-950/40 border-purple-500 text-purple-300"
                : "bg-black/40 border-zinc-800 text-zinc-400 hover:text-zinc-200"
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => {
              playNexoraXSound(240, 0.1);
              setActiveView("TASK_HISTORY");
            }}
            className={`px-3 py-1.5 rounded-xl font-mono text-[9.5px] uppercase font-bold border transition-all ${
              activeView === "TASK_HISTORY"
                ? "bg-purple-950/40 border-purple-500 text-purple-300"
                : "bg-black/40 border-zinc-800 text-zinc-400 hover:text-zinc-200"
            }`}
          >
            Task History
          </button>
          <button
            onClick={() => {
              playNexoraXSound(260, 0.1);
              setActiveView("AGENT_ANALYTICS");
            }}
            className={`px-3 py-1.5 rounded-xl font-mono text-[9.5px] uppercase font-bold border transition-all ${
              activeView === "AGENT_ANALYTICS"
                ? "bg-purple-950/40 border-purple-500 text-purple-300"
                : "bg-black/40 border-zinc-800 text-zinc-400 hover:text-zinc-200"
            }`}
          >
            Analytics
          </button>
          <button
            onClick={onShowWizard}
            className="px-3 py-1.5 bg-pink-950/20 border border-pink-500/40 text-pink-300 hover:text-white rounded-xl font-mono text-[9.5px] uppercase font-bold transition-all cursor-pointer"
          >
            &larr; Setup Wizard
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        
        {/* SCREEN 1: WORKSPACE DASHBOARD */}
        {activeView === "WORKSPACE_DASHBOARD" && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="space-y-6"
          >
            
            {/* Realtime KPI stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-black/65 p-4 border border-zinc-800 rounded-2xl relative overflow-hidden group hover:border-purple-500/25 transition-all">
                <span className="text-[8px] font-mono uppercase text-slate-500 block leading-tight font-black tracking-widest">Active Status</span>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span className="text-white text-base font-sans font-black uppercase tracking-tight">{agentStatus}</span>
                </div>
                <span className="text-[7.5px] font-mono text-zinc-500 block mt-1.5 uppercase">POLICY: {activeAgent.approvalLevel}</span>
              </div>

              <div className="bg-black/65 p-4 border border-zinc-800 rounded-2xl relative overflow-hidden group hover:border-cyan-500/25 transition-all">
                <span className="text-[8px] font-mono uppercase text-slate-500 block leading-tight font-black tracking-widest">Total Monthly Savings</span>
                <span className="text-cyan-400 text-2xl font-sans font-black tracking-tighter block mt-1">
                  {activeAgent.monthlySaved} Hours
                </span>
                <span className="text-[7.5px] font-mono text-zinc-500 block mt-1.5 uppercase">ESTIMATED £1,450 VALUE</span>
              </div>

              <div className="bg-black/65 p-4 border border-zinc-800 rounded-2xl relative overflow-hidden group hover:border-pink-500/25 transition-all">
                <span className="text-[8px] font-mono uppercase text-slate-500 block leading-tight font-black tracking-widest">Success Execution Rate</span>
                <span className="text-pink-400 text-2xl font-sans font-black tracking-tighter block mt-1">
                  98.4%
                </span>
                <span className="text-[7.5px] font-mono text-zinc-500 block mt-1.5 uppercase">PASSING SLA RULES</span>
              </div>

              <div className="bg-black/65 p-4 border border-zinc-800 rounded-2xl relative overflow-hidden group hover:border-purple-500/25 transition-all">
                <span className="text-[8px] font-mono uppercase text-slate-500 block leading-tight font-black tracking-widest">Approval Policy</span>
                <span className="text-purple-400 text-base font-sans font-black block mt-2 whitespace-nowrap uppercase">
                  {activeAgent.approvalLevel} GATE
                </span>
                <span className="text-[7.5px] font-mono text-zinc-500 block mt-1 uppercase">HUMAN-IN-THE-LOOP</span>
              </div>
            </div>

            {/* Quick action buttons & next task alert banner */}
            <div className="bg-gradient-to-r from-[#1b013a]/65 to-black border-l-4 border-pink-500 p-5 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-pink-400 animate-spin" />
                  <span className="font-mono text-[9px] uppercase tracking-wider text-white font-black">GET STARTED SECURELY</span>
                </div>
                <h4 className="text-sm font-bold text-white uppercase tracking-tight">Run your first automation sequence now</h4>
                <p className="text-xs text-zinc-400 font-sans normal-case">
                  Test-drive the agent manually to verify compliance outputs before transforming it into a fully active scheduled workflow.
                </p>
              </div>
              <button
                onClick={() => {
                  onPlaySound(440, 0.2);
                  setActiveView("RUN_FIRST_TASK");
                }}
                className="px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-mono text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-[0_0_15px_rgba(236,72,153,0.35)] cursor-pointer"
              >
                🚀 Run First Task
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left & Middle details columns */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* Deployed Active flows and tools status */}
                <div className="bg-black/75 border border-zinc-800 p-6 rounded-3xl space-y-6">
                  
                  <div className="flex justify-between items-center border-b border-zinc-900 pb-3">
                    <h3 className="text-xs font-mono font-black text-white uppercase tracking-widest flex items-center gap-2">
                      <List className="w-4 h-4 text-[#e840f3]" />
                      ACTIVE TEMPLATE ACTIONS
                    </h3>
                    <span className="text-[8.5px] bg-[#e840f3]/10 border border-[#e840f3]/25 px-2 py-0.5 rounded text-[#e840f3] font-mono tracking-widest font-black uppercase">
                      ONLINE
                    </span>
                  </div>

                  <div className="space-y-3.5">
                    {activeAgent.activeTaskFlows.map((flow: string, ix: number) => (
                      <div key={ix} className="flex items-center justify-between bg-zinc-950/80 p-4 border border-zinc-900 rounded-xl hover:border-zinc-800 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/25 flex items-center justify-center text-purple-400">
                            <Zap size={14} className="animate-pulse" />
                          </div>
                          <div>
                            <span className="text-xs font-bold text-white uppercase tracking-tight block">{flow}</span>
                            <span className="text-[8.5px] text-zinc-500 font-mono">AUTONOMOUS TRIGGER READY</span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedTaskFlow({ title: flow, risk: "LOW" });
                            setTaskFormData({
                              ...taskFormData,
                              title: flow,
                              goal: `Secure corporate resolution of ${flow}`,
                              context: `Operating for ${businessName} platform. Tone is structured to be ${toneStyle}.`
                            });
                            setActiveView("TASK_INPUT_FORM");
                          }}
                          className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white font-mono text-[8.5px] uppercase font-bold rounded-lg transition-colors cursor-pointer"
                        >
                          Trigger
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Connected safe tools card */}
                  <div className="pt-4 border-t border-zinc-900">
                    <h4 className="text-[10px] font-mono font-extrabold text-zinc-400 uppercase tracking-widest mb-3.5 flex items-center gap-1.5">
                      <Settings className="w-3.5 h-3.5 text-cyan-400" />
                      CONNECTED SYSTEM CONNECTOR CARDS
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {activeAgent.toolsList.map((tool: string, ix: number) => (
                        <div key={ix} className="flex items-center justify-between bg-[#04091a] p-3.5 border border-cyan-500/10 rounded-xl">
                          <div className="flex items-center gap-2.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]"></span>
                            <span className="text-[11px] font-bold text-slate-100 uppercase mt-0.5">{tool}</span>
                          </div>
                          <span className="text-[8px] bg-cyan-500/10 border border-cyan-500/35 text-cyan-400 uppercase font-mono px-1.5 py-0.5 rounded font-black">
                            Connected
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between bg-zinc-950 p-3.5 border border-dashed border-zinc-800 rounded-xl">
                        <span className="text-[10px] text-zinc-500 uppercase">Other channels secure</span>
                        <span className="text-[8px] bg-zinc-900 border border-zinc-800 text-zinc-400 uppercase font-mono px-1.5 py-0.5 rounded font-black">
                          Locked
                        </span>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Recurrent workflows summary list */}
                <div className="bg-black/75 border border-zinc-800 p-6 rounded-3xl space-y-4">
                  <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                    <h3 className="text-xs font-mono font-black text-white uppercase tracking-widest flex items-center gap-2">
                      <Clock className="w-4 h-4 text-cyan-400" />
                      RECURRING SERVICE FLOWS
                    </h3>
                    <button
                      onClick={() => setActiveView("RECURRING_WORKFLOW_CREATOR")}
                      className="text-[#e840f3] hover:text-white font-mono text-[9px] uppercase font-bold flex items-center gap-1"
                    >
                      <Plus size={10} /> Add New
                    </button>
                  </div>

                  <div className="divide-y divide-zinc-900">
                    {recurringWorkflows.map((rec: any, ix: number) => (
                      <div key={rec.id} className="py-3 flex justify-between items-center text-xs">
                        <div className="space-y-0.5 text-left">
                          <span className="font-bold text-white uppercase tracking-tight block">{rec.name}</span>
                          <span className="text-[9px] text-zinc-500 font-mono block">
                            Trigger: <span className="text-zinc-300">{rec.trigger}</span> | Freq: <span className="text-cyan-400">{rec.frequency}</span>
                          </span>
                        </div>
                        <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 font-mono text-[8.5px] uppercase font-black uppercase">
                          {rec.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

              {/* Right column sidebar */}
              <div className="space-y-6">
                
                {/* Host Control Actions Panel */}
                <div className="bg-gradient-to-b from-[#110126]/90 to-black border border-purple-500/15 p-6 rounded-3xl space-y-4 text-left">
                  <h3 className="text-xs font-mono font-black text-white uppercase tracking-widest pb-2 border-b border-zinc-900 flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-pink-400" />
                    WORKSPACE ACTIONS
                  </h3>

                  <div className="space-y-2 pt-1">
                    <button 
                      onClick={() => { playNexoraXSound(360, 0.1); setActiveView("RUN_FIRST_TASK"); }}
                      className="w-full text-center px-4 py-2.5 bg-gradient-to-r from-[#e840f3] to-[#a855f7] rounded-xl font-mono text-[9.5px] uppercase font-black tracking-wider hover:brightness-110 text-white transition-all cursor-pointer"
                    >
                      Run First Task
                    </button>
                    
                    <button 
                      onClick={() => { 
                        playNexoraXSound(220, 0.1); 
                        setSelectedTaskFlow({ title: "Custom Operational Sweep", risk: "MEDIUM" });
                        setTaskFormData({
                          ...taskFormData,
                          title: "Custom Operational Sweep",
                          goal: "General automation checklist cleanup",
                          context: "Auditing active business files."
                        });
                        setActiveView("TASK_INPUT_FORM"); 
                      }}
                      className="w-full text-center px-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl font-mono text-[9.5px] uppercase font-black tracking-wider hover:bg-zinc-800 text-zinc-300 transition-all cursor-pointer"
                    >
                      Create Custom Task
                    </button>

                    <button 
                      onClick={() => { playNexoraXSound(300, 0.1); setActiveView("TASK_HISTORY"); }}
                      className="w-full text-center px-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl font-mono text-[9.5px] uppercase font-black tracking-wider hover:bg-zinc-800 text-zinc-300 transition-all cursor-pointer"
                    >
                      View Approvals Queue
                    </button>

                    <button 
                      onClick={() => { playNexoraXSound(120, 0.1); setActiveView("AGENT_ANALYTICS"); }}
                      className="w-full text-center px-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl font-mono text-[9.5px] uppercase font-black tracking-wider hover:bg-zinc-800 text-zinc-300 transition-all cursor-pointer"
                    >
                      Open Performance Analytics
                    </button>
                  </div>

                  <div className="pt-3 border-t border-zinc-900 flex justify-between items-center text-xs">
                    <span className="text-zinc-500 font-mono tracking-tight uppercase">Agent Toggle state</span>
                    <button
                      onClick={() => {
                        const newStatus = agentStatus === "Active" ? "Paused" : "Active";
                        setAgentStatus(newStatus);
                        triggerToast(`NexoraX core status toggled to: ${newStatus.toUpperCase()}`, "info");
                        onPlaySound(newStatus === "Active" ? 520 : 180, 0.2);
                      }}
                      className={`px-2 py-1 rounded text-[8.5px] uppercase font-black font-mono transition-all ${
                        agentStatus === "Active" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400" : "bg-yellow-500/10 border border-yellow-500/30 text-yellow-400"
                      }`}
                    >
                      {agentStatus === "Active" ? "Pause Agent" : "Activate Agent"}
                    </button>
                  </div>
                </div>

                {/* Safety Shield advisory banner */}
                <div className="bg-[#1a0f02]/70 border border-amber-500/25 p-5 rounded-2xl text-left space-y-2">
                  <div className="flex items-center gap-2">
                    <ShieldAlert size={16} className="text-amber-500 shrink-0" />
                    <span className="font-mono text-[9px] uppercase tracking-widest text-amber-500 font-black">Compliance Directive</span>
                  </div>
                  <p className="text-[10px] text-zinc-400 leading-normal normal-case">
                    Real role and credential controls are enforced strictly on our server-side API cluster. Any manual modification of browser headers will trip internal safeguards instantly.
                  </p>
                </div>

              </div>

            </div>

          </motion.div>
        )}

        {/* SCREEN 2: RUN FIRST TASK RECOMMENDER */}
        {activeView === "RUN_FIRST_TASK" && (
          <motion.div
            key="run-first"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="space-y-6"
          >
            <div className="text-left space-y-1 pb-2 border-b border-zinc-800">
              <h3 className="text-lg font-black font-sans text-white uppercase tracking-tight">
                Recommended First Tasks
              </h3>
              <p className="text-xs text-zinc-400 font-sans normal-case">
                Select one of our customized task templates designed specifically for your deployed AI agent.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getRecommendedTasks().map((task: any, index: number) => (
                <div
                  key={index}
                  className="bg-black/65 border border-zinc-800 hover:border-purple-500/25 p-5 rounded-2xl relative overflow-hidden flex flex-col justify-between hover:shadow-[0_0_20px_rgba(168,85,247,0.05)] transition-all group"
                >
                  <div className="space-y-2 text-left">
                    <div className="flex justify-between items-center">
                      <span className="px-2 py-0.5 bg-purple-500/10 border border-purple-500/20 text-purple-300 font-mono text-[8.5px] rounded uppercase font-bold">
                        {task.risk} RISK
                      </span>
                      <span className="text-[9.5px] text-zinc-500 font-mono">
                        Saves ~{task.time}
                      </span>
                    </div>

                    <h4 className="text-sm font-black text-white uppercase tracking-tight pt-1 group-hover:text-purple-300 transition-colors">
                      {task.title}
                    </h4>
                    <p className="text-xs text-zinc-400 normal-case leading-relaxed">
                      {task.desc}
                    </p>
                  </div>

                  <div className="pt-4 flex justify-end">
                    <button
                      onClick={() => {
                        setSelectedTaskFlow(task);
                        setTaskFormData({
                          ...taskFormData,
                          title: task.title,
                          goal: `Initialize and run standard ${task.title} pipeline`,
                          context: `Operating under the specified active tone of ${toneStyle} and business guidelines.`
                        });
                        setActiveView("TASK_INPUT_FORM");
                      }}
                      className="px-4 py-2 bg-gradient-to-r from-purple-800 to-indigo-800 hover:from-purple-700 hover:to-indigo-700 text-white font-mono text-[9px] uppercase font-bold rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                    >
                      Start Task <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-start">
              <button
                onClick={() => setActiveView("WORKSPACE_DASHBOARD")}
                className="px-4 py-2 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white rounded-xl font-mono text-[10px] uppercase font-bold transition-all cursor-pointer"
              >
                &larr; Back to Dashboard
              </button>
            </div>
          </motion.div>
        )}

        {/* SCREEN 3: TASK INPUT FORM */}
        {activeView === "TASK_INPUT_FORM" && (
          <motion.div
            key="input-form"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="bg-black/75 border border-zinc-800 p-6 rounded-3xl space-y-6 max-w-2xl mx-auto"
          >
            <div className="text-left space-y-1 pb-3 border-b border-zinc-800">
              <span className="font-mono text-[8.5px] text-[#e840f3] uppercase font-black block tracking-wider">Configure Execution variables</span>
              <h3 className="text-lg font-black font-sans text-white uppercase tracking-tight">
                {taskFormData.title || "Run New Task"}
              </h3>
              <p className="text-xs text-zinc-400 normal-case">
                Validate and override parameters before dispatching client-side task logs to compiling servers.
              </p>
            </div>

            <form onSubmit={handleTaskFormSubmit} className="space-y-4 text-left text-xs font-mono">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-zinc-400 block font-bold uppercase text-[9px]">Task Title</label>
                  <input
                    type="text"
                    required
                    value={taskFormData.title}
                    onChange={(e) => setTaskFormData({...taskFormData, title: e.target.value})}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-zinc-400 block font-bold uppercase text-[9px]">Execution Goal</label>
                  <input
                    type="text"
                    value={taskFormData.goal}
                    onChange={(e) => setTaskFormData({...taskFormData, goal: e.target.value})}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-zinc-400 block font-bold uppercase text-[9px]">Customer / Client Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Acme Legal Corp"
                    value={taskFormData.customerName}
                    onChange={(e) => setTaskFormData({...taskFormData, customerName: e.target.value})}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-zinc-400 block font-bold uppercase text-[9px]">Desired Output Type</label>
                  <select
                    value={taskFormData.desiredOutput}
                    onChange={(e) => setTaskFormData({...taskFormData, desiredOutput: e.target.value})}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none focus:border-purple-500"
                  >
                    <option value="Draft Response">Email Draft Output</option>
                    <option value="JSON Payload">Structured Data Layout</option>
                    <option value="Compliance PDF">Formatted PDF Draft</option>
                    <option value="CRM Update payload">CRM Field Synchronizer</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-zinc-400 block font-bold uppercase text-[9px]">Source Text or Notes Context</label>
                <textarea
                  rows={3}
                  value={taskFormData.context}
                  onChange={(e) => setTaskFormData({...taskFormData, context: e.target.value})}
                  placeholder="Provide any raw inputs or messages you want the AI to analyze and draft a reply for..."
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none focus:border-purple-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-zinc-400 block font-bold uppercase text-[9px]">Active Brand Tone Preset</label>
                  <select
                    value={taskFormData.tone}
                    onChange={(e) => setTaskFormData({...taskFormData, tone: e.target.value})}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none focus:border-purple-500"
                  >
                    <option value="Professional">Professional</option>
                    <option value="Friendly">Friendly</option>
                    <option value="Premium">Premium</option>
                    <option value="Direct">Direct</option>
                    <option value="Executive">Executive</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-zinc-400 block font-bold uppercase text-[9px]">Verification Action Policy</label>
                  <div className="flex items-center gap-2.5 bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 h-[38px]">
                    <input
                      type="checkbox"
                      id="approvalRequired"
                      checked={taskFormData.approvalRequired}
                      onChange={(e) => setTaskFormData({...taskFormData, approvalRequired: e.target.checked})}
                      className="w-4 h-4 text-purple-600 border-zinc-800 bg-zinc-950 rounded focus:ring-0 cursor-pointer"
                    />
                    <label htmlFor="approvalRequired" className="text-zinc-300 text-[10px] select-none cursor-pointer uppercase font-black">
                      Enforce Human-in-the-Loop Gate
                    </label>
                  </div>
                </div>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-between">
                <button
                  type="button"
                  onClick={() => setActiveView("RUN_FIRST_TASK")}
                  className="px-4 py-2.5 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white rounded-xl uppercase font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-[#e840f3] to-purple-600 hover:from-[#f574fe] hover:to-purple-700 text-white rounded-xl uppercase font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  <Zap size={14} /> Generate High-Fidelity Draft
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* SCREEN 4: AGENT EXECUTION TIMELINE */}
        {activeView === "TASK_TIMELINE" && (
          <motion.div
            key="timeline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-black/85 border border-zinc-800 p-8 rounded-3xl max-w-2xl mx-auto space-y-8"
          >
            <div className="text-center space-y-2">
              <span className="px-3 py-1 bg-purple-950/40 border border-purple-500/25 rounded-full text-purple-400 font-mono text-[9px] tracking-widest uppercase font-black block max-w-fit mx-auto">
                COGNITIVE PROCESSING ENGINE ACTIVE
              </span>
              <h3 className="text-lg md:text-xl font-black font-sans text-white uppercase tracking-tight">
                COMPILING SECURE TASK AGENT BLUEPRINT
              </h3>
              <p className="text-xs text-zinc-500 normal-case">
                Please wait while the isolated pipeline server resolves variables, masks credentials, and prepares drafts.
              </p>
            </div>

            {/* Neon flashing timeline loading bar */}
            <div className="w-full bg-zinc-950 h-2.5 rounded-full overflow-hidden relative">
              <div 
                className="bg-gradient-to-r from-pink-500 via-[#e840f3] to-cyan-400 h-full rounded-full transition-all duration-300 shadow-[0_0_15px_rgba(232,64,243,0.7)]"
                style={{ width: `${Math.round((timelineStep / 6) * 100)}%` }}
              ></div>
              <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.25)_50%,transparent_100%)] animate-pulse"></div>
            </div>

            {/* Interactive execution grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 font-mono text-[10px]">
              {timelineSteps.slice(0, 6).map((step, ix) => {
                const status = currentTimelineStepStatus(ix);
                return (
                  <div 
                    key={step.num}
                    className={`p-4 border rounded-xl transition-all ${
                      status === "Running"
                        ? "bg-purple-950/20 border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.15)] scale-[1.01]"
                        : status === "Completed"
                        ? "bg-zinc-950/50 border-emerald-500/20 opacity-75"
                        : "bg-zinc-950 border-zinc-900 opacity-50"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-extrabold text-white uppercase">{step.num}. {step.name}</span>
                      <span className={`text-[8.5px] uppercase font-bold tracking-widest ${
                        status === "Running" ? "text-cyan-400" : status === "Completed" ? "text-emerald-400" : "text-zinc-600"
                      }`}>
                        {status === "Running" ? "● RUNNING" : status === "Completed" ? "✓ SUCCESS" : "QUEUED"}
                      </span>
                    </div>
                    <p className="text-zinc-500 text-[9px] leading-normal normal-case">{step.exp}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* SCREEN 5: OUTPUT REVIEW */}
        {activeView === "OUTPUT_REVIEW" && (
          <motion.div
            key="output-review"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="space-y-6 max-w-3xl mx-auto"
          >
            <div className="text-left space-y-1 pb-2 border-b border-zinc-800">
              <span className="font-mono text-[8.5px] text-cyan-400 uppercase font-black block tracking-wider">COMPILATION STAGE PASSED</span>
              <h3 className="text-lg font-black font-sans text-white uppercase tracking-tight">
                Review Generated Output Draft
              </h3>
              <p className="text-xs text-zinc-400 normal-case">
                Inspect AI generated draft. Direct channels are locked securely until explicit human-in-the-loop approval is completed.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-left">
              
              <div className="lg:col-span-2 space-y-4">
                
                {/* Draft content view */}
                <div className="bg-black/85 border border-zinc-800 rounded-3xl p-6 relative overflow-hidden font-mono text-xs text-zinc-300">
                  <div className="absolute top-0 right-0 p-3 text-[8px] bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 uppercase font-black tracking-widest rounded-bl-xl">
                    DRAFT READY
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-xs bg-zinc-950 p-4 rounded-2xl text-left text-slate-300 border border-zinc-900 leading-relaxed font-mono">
                    {generatedOutput}
                  </pre>
                </div>

                {/* Direct action triggers */}
                <div className="bg-[#1b013a]/30 border border-purple-500/20 rounded-2xl p-4 flex justify-between items-center text-xs">
                  <div className="space-y-0.5">
                    <span className="font-bold text-white block uppercase text-[10px]">Connected service output preview</span>
                    <span className="text-zinc-500 font-mono text-[9px]">Platform destination: {activeAgent.toolsList[0] || "Client Portal Outbox"}</span>
                  </div>
                  <span className="px-2 py-0.5 bg-yellow-500/10 border border-yellow-500/35 text-yellow-400 font-mono text-[8px] uppercase font-black">
                    Awaiting Human approval
                  </span>
                </div>

              </div>

              {/* Sidebar review metrics */}
              <div className="space-y-4">
                <div className="bg-black/65 border border-zinc-800 p-5 rounded-2xl space-y-4 text-xs font-mono">
                  <span className="font-bold text-white block uppercase text-[9px] tracking-widest text-zinc-500">AUDIT TELEMETRY</span>
                  
                  <div className="space-y-3 dark">
                    <div className="flex justify-between items-center py-1 border-b border-zinc-900">
                      <span className="text-zinc-400">Confidence Match:</span>
                      <span className="text-emerald-400 font-bold">98% PASS</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-zinc-900">
                      <span className="text-zinc-400">Risk Matrix Factor:</span>
                      <span className="text-yellow-400 font-bold">MEDIUM GATE</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-zinc-900">
                      <span className="text-zinc-400">Credential Guard:</span>
                      <span className="text-cyan-400 font-bold">MASKED OK</span>
                    </div>
                  </div>

                  <div className="space-y-2 pt-2">
                    <button
                      onClick={handleApproveAction}
                      className="w-full text-center px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl uppercase font-black uppercase text-[9.5px] font-mono tracking-widest cursor-pointer"
                    >
                      Approve & Dispatched
                    </button>
                    <button
                      onClick={() => {
                        triggerToast("Regenerating a fresh draft variant with creative jitter", "info");
                        onPlaySound(440, 0.2);
                        startTimelineSimulation({ title: taskFormData.title });
                      }}
                      className="w-full text-center px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 rounded-xl uppercase font-bold text-[9px] cursor-pointer"
                    >
                      Regenerate draft
                    </button>
                    <button
                      onClick={() => {
                        triggerToast("Direct manual edit override. Field fully writable.", "info");
                        onPlaySound(220, 0.1);
                      }}
                      className="w-full text-center px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 rounded-xl uppercase font-bold text-[9px] cursor-pointer"
                    >
                      Edit draft text
                    </button>
                    <button
                      onClick={() => {
                        onPlaySound(150, 0.3);
                        triggerToast("Execution sequence canceled and draft trashed", "warning");
                        setActiveView("RUN_FIRST_TASK");
                      }}
                      className="w-full text-center px-4 py-2 text-rose-400 hover:text-white rounded-xl font-bold text-[9px] cursor-pointer"
                    >
                      Trash Draft
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-zinc-950 border border-zinc-900 rounded-xl font-mono text-[9.5px] leading-relaxed text-zinc-500 text-left">
                  <span>The custom agent was restricted from sending direct SMTP queries or publishing to integrations because the approval policies are set to strict.</span>
                </div>
              </div>

            </div>
          </motion.div>
        )}

        {/* SCREEN 6: HUMAN APPROVAL GATE */}
        {activeView === "HUMAN_APPROVAL_GATE" && (
          <motion.div
            key="approval-gate"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            className="bg-black border-2 border-amber-500/30 p-8 rounded-3xl max-w-2xl mx-auto space-y-6 text-left"
          >
            <div className="flex justify-between items-start border-b border-zinc-900 pb-4">
              <div className="space-y-1 block">
                <span className="font-mono text-[8px] tracking-[0.4em] text-amber-400 font-extrabold uppercase block leading-tight">
                  SECURITY POLICY COMPLIANCE ADVISORY
                </span>
                <h3 className="text-lg md:text-xl font-bold font-mono text-white uppercase tracking-tight">
                  Human Approval Gate Activator
                </h3>
              </div>
              <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <ShieldAlert size={20} className="text-amber-500 animate-pulse" />
              </div>
            </div>

            <p className="text-xs text-zinc-300 leading-relaxed normal-case">
              This task triggers an external transaction trigger or contact contact query (<strong>{taskFormData.title}</strong>). Because the action impacts client record files or SMTP distribution, direct clearance requires manual Human Check confirmation.
            </p>

            <div className="p-4 bg-[#140b02] border border-amber-500/20 rounded-xl space-y-3 font-mono text-xs">
              <div className="flex justify-between text-[11px] border-b border-zinc-900 pb-2">
                <span className="text-zinc-500 uppercase font-bold">Action parameters</span>
                <span className="text-amber-400 font-black">HIGH VALUE GATE</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div className="space-y-0.5">
                  <span className="text-zinc-500">AFFECTED CONNECTION:</span>
                  <p className="text-white font-bold">{activeAgent.toolsList[0] || "Secure SMTP Outbox"}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-zinc-500">BRAND ALIGNMENT:</span>
                  <p className="text-white font-bold">{toneStyle.toUpperCase()} PRESET</p>
                </div>
              </div>
            </div>

            <div className="space-y-1.5 font-mono text-xs">
              <label className="text-zinc-400 block font-bold uppercase text-[9px]">Approval / Revision Comments (Recorded to secure ledger)</label>
              <textarea
                rows={2}
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                placeholder="Optionally write modification feedback or internal clearance comments..."
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white font-sans focus:outline-none"
              />
            </div>

            <div className="pt-2 flex flex-wrap gap-2.5 justify-end">
              <button
                onClick={() => {
                  onPlaySound(120, 0.2);
                  triggerToast("Approval rejected. Operation aborted and logged.", "error");
                  setActiveView("WORKSPACE_DASHBOARD");
                }}
                className="px-4 py-2 bg-zinc-900 border border-zinc-800 text-rose-400 hover:text-white rounded-xl uppercase font-mono text-[9px] font-bold cursor-pointer"
              >
                Reject action
              </button>
              <button
                onClick={() => {
                  finalizeTaskCompletion("Approved & Completed");
                }}
                className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl uppercase font-mono text-[9px] font-black tracking-widest cursor-pointer"
              >
                Approve and Dispatch
              </button>
            </div>
          </motion.div>
        )}

        {/* SCREEN 7: TASK COMPLETED SUCCESS */}
        {activeView === "TASK_COMPLETED_SUCCESS" && (
          <motion.div
            key="success-screen"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-black/85 border border-[#e840f3]/30 p-8 rounded-3xl max-w-2xl mx-auto space-y-6 text-center"
          >
            <div className="relative w-20 h-20 mx-auto rounded-full bg-gradient-to-tr from-[#e840f3] to-purple-600 flex items-center justify-center p-0.5 shadow-[0_0_30px_rgba(232,64,243,0.3)] animate-pulse">
              <div className="w-full h-full bg-black rounded-full flex items-center justify-center text-emerald-400">
                <CheckCircle size={36} />
              </div>
            </div>

            <div className="space-y-1.5">
              <span className="text-[10px] uppercase font-bold tracking-[0.3em] text-cyan-400 block font-mono">TASK COMPLETED SECURELY</span>
              <h3 className="text-xl md:text-2xl font-black font-sans text-white uppercase tracking-tight">
                Task Completed Successfully
              </h3>
              <p className="text-xs text-zinc-400 leading-relaxed max-w-md mx-auto normal-case">
                Your AI agent has completed its first task safely and securely with your approval controls fully adhered to.
              </p>
            </div>

            <div className="bg-[#110126]/60 p-5 border border-zinc-800 rounded-2xl relative overflow-hidden text-left font-mono text-xs text-slate-300 space-y-2 max-w-md mx-auto">
              <div className="flex justify-between py-1 border-b border-zinc-900">
                <span>TASK NAME:</span>
                <span className="text-white font-extrabold truncate max-w-[170px] uppercase">{taskFormData.title || "Custom Autonomous Process"}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-zinc-900">
                <span>TIME SAVED:</span>
                <span className="text-emerald-400 font-extrabold">30 MINUTES SAVED</span>
              </div>
              <div className="flex justify-between py-1 border-b border-zinc-900">
                <span>APPROVAL STATE:</span>
                <span className="text-cyan-400 font-extrabold uppercase">HUMAN REVIEW PASSED</span>
              </div>
              <div className="flex justify-between py-1">
                <span>COMPLETION TIME:</span>
                <span className="text-white">Just Now (SLA SLA-0.8s)</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 justify-center pt-2">
              <button
                onClick={() => {
                  onPlaySound(220, 0.1);
                  setTaskFormData({
                    title: "",
                    goal: "",
                    context: "",
                    customerName: "",
                    sourceText: "",
                    desiredOutput: "Draft Response",
                    tone: toneStyle,
                    deadline: "End of Day",
                    approvalRequired: true,
                    notes: ""
                  });
                  setActiveView("RUN_FIRST_TASK");
                }}
                className="px-5 py-2.5 bg-gradient-to-r from-purple-800 to-indigo-800 hover:from-purple-700 hover:to-indigo-700 text-white font-mono text-[9px] uppercase font-black rounded-lg transition-colors cursor-pointer"
              >
                Run Another Task
              </button>
              <button
                onClick={() => {
                  onPlaySound(200, 0.1);
                  setActiveView("TASK_HISTORY");
                }}
                className="px-5 py-2.5 bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white font-mono text-[9px] uppercase font-black rounded-lg transition-colors cursor-pointer"
              >
                View History Registry
              </button>
              <button
                onClick={() => {
                  onPlaySound(340, 0.15);
                  // Setup inputs for recurring automation step 10
                  setRecurringName(`${taskFormData.title || "Custom Active Task"} Recurring Automator`);
                  setActiveView("RECURRING_WORKFLOW_CREATOR");
                }}
                className="px-5 py-2.5 bg-pink-950/20 border border-pink-500 hover:bg-pink-950/40 text-pink-300 hover:text-white font-mono text-[9px] uppercase font-black rounded-lg transition-colors cursor-pointer"
              >
                Create Recurring Workflow
              </button>
            </div>
          </motion.div>
        )}

        {/* SCREEN 8: TASK HISTORY */}
        {activeView === "TASK_HISTORY" && (
          <motion.div
            key="history"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-zinc-800 pb-3">
              <div className="text-left space-y-0.5">
                <h3 className="text-lg font-black font-sans text-white uppercase tracking-tight">Task execution log registry</h3>
                <p className="text-xs text-zinc-400 font-sans normal-case">
                  Track the telemetry outputs and approval steps of actions processed securely in this workspace.
                </p>
              </div>

              {/* Status Filters */}
              <div className="flex flex-wrap gap-1 bg-black/60 p-1 rounded-xl border border-zinc-800 font-mono text-[8.5px]">
                {["ALL", "COMPLETED", "ESCALATED", "DRAFT"].map((f) => (
                  <button
                    key={f}
                    onClick={() => {
                      onPlaySound(180, 0.05);
                      setHistoryFilter(f);
                    }}
                    className={`px-2.5 py-1 rounded transition-colors uppercase font-bold cursor-pointer ${
                      historyFilter === f ? "bg-purple-600 text-white" : "text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* List items */}
            <div className="space-y-3.5">
              {taskHistoryList
                .filter((item) => historyFilter === "ALL" || item.status === historyFilter)
                .map((item) => (
                  <div
                    key={item.id}
                    className="bg-black/65 border border-zinc-800 p-4 rounded-2xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:border-zinc-700 transition-colors"
                  >
                    <div className="text-left space-y-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${item.status === "COMPLETED" ? "bg-emerald-500" : "bg-rose-500"}`}></span>
                        <span className="font-mono text-[9px] text-[#e840f3] font-bold tracking-widest">{item.id}</span>
                        <span className="text-zinc-500 text-[9px] font-mono">SLA TRIGGER PASS</span>
                      </div>
                      <h4 className="text-sm font-black text-white uppercase tracking-tight">{item.title}</h4>
                      <div className="flex flex-wrap gap-2 text-[9px] font-mono">
                        <span className="text-zinc-400">Agent:</span> <span className="text-zinc-300">{item.agent}</span>
                        <span className="text-zinc-500">|</span>
                        <span className="text-zinc-400">Type:</span> <span className="text-cyan-400">{item.outputType}</span>
                        <span className="text-zinc-500">|</span>
                        <span className="text-zinc-400">Saved:</span> <span className="text-emerald-400">{item.timeSaved}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5 self-end sm:self-auto">
                      <span className={`px-2.5 py-0.5 rounded text-[8.5px] font-mono font-black border uppercase ${
                        item.approvalStatus === "APPROVED" || item.approvalStatus === "COMPLETED_BY_ADMIN"
                          ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-400"
                          : "bg-red-500/10 border-red-500/25 text-red-400"
                      }`}>
                        {item.approvalStatus}
                      </span>
                      <button
                        onClick={() => {
                          onPlaySound(220, 0.1);
                          triggerToast(`Task ${item.id} details are securely archived server-side.`, "info");
                        }}
                        className="px-3 py-1 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 hover:text-white font-mono text-[9px] uppercase font-bold rounded-lg cursor-pointer"
                      >
                        Inspect details
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </motion.div>
        )}

        {/* SCREEN 9: AGENT ANALYTICS */}
        {activeView === "AGENT_ANALYTICS" && (
          <motion.div
            key="analytics"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="text-left space-y-1 pb-3 border-b border-zinc-800">
              <h3 className="text-lg font-black font-sans text-white uppercase tracking-tight">
                Client Performance and Savings Analytics
              </h3>
              <p className="text-xs text-zinc-400 font-sans normal-case">
                Monitor execution volumes, direct time saved variables, and active approval compliance statistics.
              </p>
            </div>

            {/* KPI grid counts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-black/65 p-4 border border-zinc-800 rounded-2xl relative overflow-hidden">
                <span className="text-[8px] font-mono uppercase text-slate-500 block leading-tight font-black tracking-widest text-left">Tasks Completed</span>
                <span className="text-white text-3xl font-sans font-black block mt-1.5 text-left">142</span>
                <span className="text-[7.5px] font-mono text-emerald-400 block mt-1 uppercase text-left">&uarr; 12% This Week</span>
              </div>
              <div className="bg-black/65 p-4 border border-zinc-800 rounded-2xl relative overflow-hidden">
                <span className="text-[8px] font-mono uppercase text-slate-500 block leading-tight font-black tracking-widest text-left">Total Hours Saved</span>
                <span className="text-cyan-400 text-3xl font-sans font-black block mt-1.5 text-left">74.5 Hours</span>
                <span className="text-[7.5px] font-mono text-zinc-500 block mt-1 uppercase text-left">EST. VALUE £2,650</span>
              </div>
              <div className="bg-black/65 p-4 border border-zinc-800 rounded-2xl relative overflow-hidden">
                <span className="text-[8px] font-mono uppercase text-slate-500 block leading-tight font-black tracking-widest text-left">Approval Gate Delay</span>
                <span className="text-purple-400 text-3xl font-sans font-black block mt-1.5 text-left">2.4 mins</span>
                <span className="text-[7.5px] font-mono text-zinc-500 block mt-1 uppercase text-left">SLA CRITERIA: SLA-0.8S</span>
              </div>
              <div className="bg-black/65 p-4 border border-zinc-800 rounded-2xl relative overflow-hidden">
                <span className="text-[8px] font-mono uppercase text-slate-500 block leading-tight font-black tracking-widest text-left">Failed / Escalated</span>
                <span className="text-pink-400 text-3xl font-sans font-black block mt-1.5 text-left">2 / 1</span>
                <span className="text-[7.5px] font-mono text-emerald-400 block mt-1 uppercase text-left">SAFEGUARD TRIGGERED</span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-left">
              
              {/* Left chart block using simple clean elegant responsive visual bars */}
              <div className="bg-black/75 border border-zinc-800 p-6 rounded-3xl space-y-4">
                <h4 className="text-xs font-mono font-black text-white uppercase tracking-widest">
                  Tasks Executed by category and workflow
                </h4>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <div className="flex justify-between text-[10px] font-mono">
                      <span className="text-zinc-400">Customer Support Replies</span>
                      <span className="text-white font-bold">58 Tasks (43%)</span>
                    </div>
                    <div className="w-full bg-zinc-950 h-2 rounded-full overflow-hidden">
                      <div className="bg-pink-500 h-full rounded-full" style={{ width: "43%" }}></div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-[10px] font-mono">
                      <span className="text-zinc-400">CRM Field / Client Record Synchronization</span>
                      <span className="text-white font-bold">38 Tasks (27%)</span>
                    </div>
                    <div className="w-full bg-zinc-950 h-2 rounded-full overflow-hidden">
                      <div className="bg-cyan-400 h-full rounded-full" style={{ width: "27%" }}></div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-[10px] font-mono">
                      <span className="text-zinc-400">High-Fidelity Document Drafting</span>
                      <span className="text-white font-bold text-slate-300">26 Tasks (18%)</span>
                    </div>
                    <div className="w-full bg-zinc-950 h-2 rounded-full overflow-hidden">
                      <div className="bg-[#e840f3] h-full rounded-full" style={{ width: "18%" }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right advisory recommender block */}
              <div className="bg-gradient-to-b from-purple-950/10 to-black border border-purple-500/10 p-6 rounded-3xl space-y-4">
                <h4 className="text-xs font-mono font-black text-white uppercase tracking-widest flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-pink-400 animate-spin" />
                  NexoraX Intelligent Optimization Suggestions
                </h4>

                <div className="space-y-3.5 font-sans text-xs">
                  <div className="bg-zinc-950 border border-zinc-900 p-3 rounded-xl flex items-start gap-2.5 normal-case">
                    <span className="p-1 px-2 bg-pink-500/10 rounded font-mono text-[9px] text-pink-400 font-extrabold block">OPT-01</span>
                    <div>
                      <p className="text-slate-200 font-bold">Turn &quot;{activeAgent.activeTaskFlows[0]}&quot; into recurring scheduling</p>
                      <p className="text-zinc-500 text-[11px] mt-0.5">Automating this manually triggered task flow is forecasted to save an additional 3 hours per week.</p>
                    </div>
                  </div>

                  <div className="bg-zinc-950 border border-zinc-900 p-3 rounded-xl flex items-start gap-2.5 normal-case">
                    <span className="p-1 px-2 bg-cyan-400/10 rounded font-mono text-[9px] text-cyan-400 font-extrabold block">OPT-02</span>
                    <div>
                      <p className="text-slate-200 font-bold">Connect your pending communication channel</p>
                      <p className="text-zinc-500 text-[11px] mt-0.5">Your active setup lacks a verified Slack connection. Activating this will streamline team notifications.</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </motion.div>
        )}

        {/* SCREEN 10: RECURRING WORKFLOW CREATOR */}
        {activeView === "RECURRING_WORKFLOW_CREATOR" && (
          <motion.div
            key="recurring"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="bg-black/75 border border-zinc-800 p-6 rounded-3xl space-y-6 max-w-2xl mx-auto"
          >
            <div className="text-left space-y-1 pb-3 border-b border-zinc-800">
              <span className="font-mono text-[8.5px] text-[#e840f3] uppercase font-black block tracking-wider">SECURE PIPELINE CONSOLE</span>
              <h3 className="text-lg font-black font-sans text-white uppercase tracking-tight">
                Create Recurring Automation Workflow
              </h3>
              <p className="text-xs text-zinc-400 normal-case">
                Convert manual task execution sequences into structural cron routines with active security compliance gates.
              </p>
            </div>

            <form onSubmit={handleCreateRecurringWorkflow} className="space-y-4 text-left text-xs font-mono">
              <div className="space-y-1.5">
                <label className="text-zinc-400 block font-bold uppercase text-[9px]">Workflow Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Daily Support Swear Routine"
                  value={recurringName}
                  onChange={(e) => setRecurringName(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-zinc-400 block font-bold uppercase text-[9px]">Agent Template Assigned</label>
                  <input
                    type="text"
                    disabled
                    value={activeAgent.name}
                    className="w-full bg-zinc-900 border border-zinc-800 text-zinc-500 rounded-xl px-3.5 py-2 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[#f574fe] block font-bold uppercase text-[9px]">Core Trigger Mode</label>
                  <select
                    value={recurringTrigger}
                    onChange={(e) => setRecurringTrigger(e.target.value)}
                    className="w-full bg-zinc-950 border border-[#e840f3]/30 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none"
                  >
                    <option value="Daily at Scheduled hour">Manual Trigger Execution</option>
                    <option value="Daily block scheduled">Daily cron trigger cycle</option>
                    <option value="Weekly audit trigger">Weekly audit sweep sequence</option>
                    <option value="On new webhook dispatch">On new incoming webhook packet</option>
                    <option value="On new form submitted packet">On new website form submission</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-zinc-400 block font-bold uppercase text-[9px]">Automation Frequency</label>
                  <input
                    type="text"
                    value={recurringFrequency}
                    onChange={(e) => setRecurringFrequency(e.target.value)}
                    placeholder="e.g. Every hour or daily at 9:00 AM"
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-zinc-400 block font-bold uppercase text-[9px]">Approval Guard Rule</label>
                  <select
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-white font-sans text-xs focus:outline-none"
                  >
                    <option>Enforce approval check on HIGH risk only</option>
                    <option>Enforce manual review before all dispatches</option>
                    <option>Full autopilot (Notify when complete)</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-between">
                <button
                  type="button"
                  onClick={() => setActiveView("WORKSPACE_DASHBOARD")}
                  className="px-4 py-2.5 bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white rounded-xl uppercase font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl uppercase font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  Create active Recurring Workflow
                </button>
              </div>
            </form>
          </motion.div>
        )}

      </AnimatePresence>

      
      {/* Dynamic play audio wrapper helper */}
      <div className="pt-4 border-t border-zinc-900 text-[10px] font-mono text-zinc-500 flex justify-between items-center bg-black/35 p-3 rounded-xl border border-zinc-900 leading-normal">
        <span>NexoraX Security Defense Node Level-5 Shield active tracking credentials... </span>
        <span className="text-[#e840f3] uppercase font-bold">SECURE CHANNEL</span>
      </div>

    </div>
  );
}

// Simple synth audio generator
function playNexoraXSound(frequency: number, duration: number, type: string = "sine") {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = type as OscillatorType;
    osc.frequency.value = frequency;
    
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch (e) {
    // browser permissions block node audio context
  }
}
